import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Flag, ArrowLeft, ArrowRight, UploadSimple, Trash, CheckCircle, MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_DATA = {
  client_id: null, client_name: "", client_address: "",
  client_contact: "", client_contact_schedule: "",
  improvement_suggestions: "",
  supply_doc_type: null, supply_doc_number: "", supply_doc_date: "",
  complaint_description: "",
  observations: "",
  product_reference: "", product_name: "", product_quantity_m2: "",
  control: "", quality_grade: null, caliber: "", tonality: "",
  work_address: "", work_contact: "",
  application_environment: null, application_surface: null,
  application_date: "",
  samples_type: "", samples_location: "",
  work_visit_done: null, work_visit_date: "", work_visit_time: "",
  seller_opinion: "",
};


const ChoiceButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
      active
        ? "border-[#009392] bg-[#009392]/10 text-[#009392] scale-[1.02]"
        : "border-stone-200 text-stone-600 hover:border-stone-300"
    }`}
  >
    {children}
  </button>
);

const FieldLabel = ({ children }) => (
  <Label className="text-sm font-semibold text-stone-700 mb-1.5 block">{children}</Label>
);

export default function ReclamacaoWizard() {
  const navigate = useNavigate();
  const { complaintId: routeComplaintId } = useParams();
  const isEditMode = !!routeComplaintId;
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL_DATA);
  const [submitting, setSubmitting] = useState(false);
  const [complaintId, setComplaintId] = useState(routeComplaintId || null);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [loadingComplaint, setLoadingComplaint] = useState(isEditMode);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientSearchDebounce = useRef(null);

  const setField = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const handleClientSearchChange = (term) => {
    setClientSearchTerm(term);
    setShowClientDropdown(true);
    if (clientSearchDebounce.current) clearTimeout(clientSearchDebounce.current);
    if (!term.trim()) {
      setClientResults([]);
      return;
    }
    clientSearchDebounce.current = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const response = await axios.get(`${API}/clients`, { params: { search: term.trim() } });
        setClientResults(response.data.slice(0, 8));
      } catch (error) {
        console.error("Error searching clients:", error);
      } finally {
        setSearchingClients(false);
      }
    }, 300);
  };

  const handleSelectClient = (client) => {
    const addressParts = [client.address, client.postal_code, client.city].filter(Boolean);
    const contact = client.phone || (client.contacts && client.contacts[0]?.phone) || "";
    setData(prev => ({
      ...prev,
      client_id: client.id,
      client_name: client.name || prev.client_name,
      client_address: addressParts.join(', ') || prev.client_address,
      client_contact: contact || prev.client_contact,
    }));
    setClientSearchTerm("");
    setClientResults([]);
    setShowClientDropdown(false);
    toast.success(`Cliente "${client.name}" selecionado`);
  };


  const toPtDate = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : isoDate;
  };

  const fromPtDate = (ptDate) => {
    if (!ptDate) return "";
    const [d, m, y] = ptDate.split('/');
    return (y && m && d) ? `${y}-${m}-${d}` : "";
  };

  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const response = await axios.get(`${API}/complaints/${routeComplaintId}`);
        const c = response.data;
        setData({
          client_id: c.client_id || null,
          client_name: c.client_name || "",
          client_address: c.client_address || "",
          client_contact: c.client_contact || "",
          client_contact_schedule: c.client_contact_schedule || "",
          improvement_suggestions: c.improvement_suggestions || "",
          supply_doc_type: c.supply_doc_type || null,
          supply_doc_number: c.supply_doc_number || "",
          supply_doc_date: fromPtDate(c.supply_doc_date),
          complaint_description: c.complaint_description || "",
          observations: c.observations || "",
          product_reference: c.product_reference || "",
          product_name: c.product_name || "",
          product_quantity_m2: c.product_quantity_m2 ?? "",
          control: c.control || "",
          quality_grade: c.quality_grade || null,
          caliber: c.caliber || "",
          tonality: c.tonality || "",
          work_address: c.work_address || "",
          work_contact: c.work_contact || "",
          application_environment: c.application_environment || null,
          application_surface: c.application_surface || null,
          application_date: fromPtDate(c.application_date),
          samples_type: c.samples_type || "",
          samples_location: c.samples_location || "",
          work_visit_done: !!c.work_visit_done,
          work_visit_date: fromPtDate(c.work_visit_date),
          work_visit_time: c.work_visit_time || "",
          seller_opinion: c.seller_opinion || "",
        });
        setPhotos(c.photos || []);
      } catch (error) {
        console.error("Error loading complaint for edit:", error);
        toast.error("Erro ao carregar a reclamação");
      } finally {
        setLoadingComplaint(false);
      }
    })();
  }, [isEditMode, routeComplaintId]);

  const STEPS_META = [
    { title: "Dados do Cliente", desc: "Nome e morada do cliente que está a reclamar" },
    { title: "Contacto do Cliente", desc: "Para podermos agendar uma visita, se necessário" },
    { title: "Documento de Fornecimento", desc: "Tipo, número e data do documento" },
    { title: "Descrição da Reclamação", desc: "Descrição sumária e objetiva / tipo de defeito" },
    { title: "Observações Adicionais", desc: "Informação extra relevante (opcional)" },
    { title: "Identificação do Produto", desc: "Referência, nome e quantidade" },
    { title: "Dados da Caixa do Produto", desc: "Controlo, qualidade, calibre e tonalidade" },
    { title: "Morada da Obra", desc: "Local onde o produto foi/vai ser aplicado" },
    { title: "Contacto do Responsável da Obra", desc: "Pessoa a contactar na obra" },
    { title: "Local de Aplicação", desc: "Onde o produto foi aplicado" },
    { title: "Data de Aplicação", desc: "Quando o produto foi aplicado" },
    { title: "Amostras Recolhidas", desc: "Tipo e local de recolha (opcional)" },
    { title: "Visita à Obra", desc: "Houve visita à obra? Se sim, data e hora" },
    { title: "Parecer do Vendedor", desc: "Avaliação do vendedor sobre a reclamação (opcional)" },
    { title: "Sugestões do Cliente", desc: "Alguma sugestão mencionada pelo cliente (opcional)" },
    { title: "Fotografias", desc: "Anexe fotos que mostrem o defeito" },
  ];
  const totalSteps = STEPS_META.length;

  const validateStep = () => {
    if (step === 0 && !data.client_name.trim()) {
      toast.error("Indique o nome do cliente");
      return false;
    }
    if (step === 3 && !data.complaint_description.trim()) {
      toast.error("Descreva a reclamação");
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (step < totalSteps - 1) setStep(step + 1);
  };
  const goBack = () => { if (step > 0) setStep(step - 1); };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        product_quantity_m2: data.product_quantity_m2 ? parseFloat(data.product_quantity_m2) : null,
        supply_doc_date: toPtDate(data.supply_doc_date),
        application_date: toPtDate(data.application_date),
        work_visit_done: !!data.work_visit_done,
        work_visit_date: data.work_visit_done ? toPtDate(data.work_visit_date) : "",
      };
      if (isEditMode) {
        await axios.put(`${API}/complaints/${routeComplaintId}`, payload);
        toast.success("Reclamação atualizada com sucesso");
        navigate(`/reclamacoes/${routeComplaintId}`);
      } else {
        const response = await axios.post(`${API}/complaints`, payload);
        setComplaintId(response.data.id);
        toast.success("Reclamação registada com sucesso");
      }
    } catch (error) {
      console.error("Error submitting complaint:", error);
      toast.error(isEditMode ? "Erro ao atualizar a reclamação" : "Erro ao registar a reclamação");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !complaintId) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/complaints/${complaintId}/photos/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPhotos(prev => [...prev, response.data]);
      }
      toast.success(`${files.length} foto(s) anexada(s)`);
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Erro ao anexar foto");
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/complaints/${complaintId}/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Erro ao remover foto");
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="relative">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  value={clientSearchTerm}
                  onChange={(e) => handleClientSearchChange(e.target.value)}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                  placeholder="Escreva o nome do cliente para pesquisar..."
                  className="pl-9"
                  data-testid="wizard-client-search"
                />
              </div>
              {showClientDropdown && clientSearchTerm.trim() && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="wizard-client-search-results">
                  {searchingClients ? (
                    <p className="px-4 py-3 text-sm text-stone-400">A pesquisar...</p>
                  ) : clientResults.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-stone-400">Nenhum cliente encontrado - pode preencher manualmente abaixo</p>
                  ) : (
                    clientResults.map((client) => (
                      <button
                        type="button"
                        key={client.id}
                        onClick={() => handleSelectClient(client)}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#009392]/10 border-b border-stone-100 last:border-0 transition-colors"
                        data-testid={`wizard-client-result-${client.id}`}
                      >
                        <p className="text-sm font-semibold text-stone-800">{client.name}</p>
                        {client.city && (
                          <p className="text-xs text-stone-400 flex items-center gap-1 mt-0.5">
                            <MapPin size={11} /> {client.city}
                          </p>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <FieldLabel>Nome do Cliente *</FieldLabel>
              <Input value={data.client_name} onChange={(e) => setField('client_name', e.target.value)} placeholder="Nome completo do cliente" data-testid="wizard-client-name" />
            </div>
            <div>
              <FieldLabel>Morada</FieldLabel>
              <Textarea value={data.client_address} onChange={(e) => setField('client_address', e.target.value)} placeholder="Morada completa" data-testid="wizard-client-address" />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <FieldLabel>Contacto do Cliente</FieldLabel>
              <Input value={data.client_contact} onChange={(e) => setField('client_contact', e.target.value)} placeholder="Telefone ou email" data-testid="wizard-client-contact" />
            </div>
            <div>
              <FieldLabel>Horário de Disponibilidade para Visita</FieldLabel>
              <Input value={data.client_contact_schedule} onChange={(e) => setField('client_contact_schedule', e.target.value)} placeholder="Ex: Tardes, entre as 14h e as 18h" data-testid="wizard-client-schedule" />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <FieldLabel>Tipo de Documento</FieldLabel>
              <div className="flex flex-wrap gap-3">
                <ChoiceButton active={data.supply_doc_type === 'factura'} onClick={() => setField('supply_doc_type', 'factura')} testId="wizard-doc-factura">Factura</ChoiceButton>
                <ChoiceButton active={data.supply_doc_type === 'vale_dinheiro'} onClick={() => setField('supply_doc_type', 'vale_dinheiro')} testId="wizard-doc-vale">Venda a Dinheiro</ChoiceButton>
                <ChoiceButton active={data.supply_doc_type === 'guia_remessa'} onClick={() => setField('supply_doc_type', 'guia_remessa')} testId="wizard-doc-guia">Guia Remessa</ChoiceButton>
              </div>
            </div>
            <div>
              <FieldLabel>Número do Documento</FieldLabel>
              <Input value={data.supply_doc_number} onChange={(e) => setField('supply_doc_number', e.target.value)} placeholder="Ex: FT2026/001234" data-testid="wizard-doc-number" />
            </div>
            <div>
              <FieldLabel>Data do Documento</FieldLabel>
              <Input type="date" value={data.supply_doc_date} onChange={(e) => setField('supply_doc_date', e.target.value)} data-testid="wizard-doc-date" />
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <FieldLabel>Descrição Sumária e Objetiva da Reclamação / Tipo de Defeito *</FieldLabel>
            <Textarea value={data.complaint_description} onChange={(e) => setField('complaint_description', e.target.value)} placeholder="Descreva o defeito ou problema reportado" rows={5} data-testid="wizard-complaint-description" />
          </div>
        );
      case 4:
        return (
          <div>
            <FieldLabel>Observações Adicionais (opcional)</FieldLabel>
            <Textarea value={data.observations} onChange={(e) => setField('observations', e.target.value)} placeholder="Qualquer informação adicional relevante" rows={5} data-testid="wizard-observations" />
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <div>
              <FieldLabel>Referência do Produto</FieldLabel>
              <Input value={data.product_reference} onChange={(e) => setField('product_reference', e.target.value)} placeholder="Ex: GP0625" data-testid="wizard-product-reference" />
            </div>
            <div>
              <FieldLabel>Nome do Produto</FieldLabel>
              <Input value={data.product_name} onChange={(e) => setField('product_name', e.target.value)} placeholder="Ex: Roriz Bege" data-testid="wizard-product-name" />
            </div>
            <div>
              <FieldLabel>Quantidade (m²)</FieldLabel>
              <Input type="number" step="0.01" value={data.product_quantity_m2} onChange={(e) => setField('product_quantity_m2', e.target.value)} placeholder="0,00" data-testid="wizard-product-quantity" />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-4">
            <p className="text-xs text-stone-400">Estes dados encontram-se impressos na caixa do produto</p>
            <div>
              <FieldLabel>Controlo</FieldLabel>
              <Input value={data.control} onChange={(e) => setField('control', e.target.value)} data-testid="wizard-control" />
            </div>
            <div>
              <FieldLabel>Qualidade</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {["1ª", "2ª", "3ª"].map(q => (
                  <ChoiceButton key={q} active={data.quality_grade === q} onClick={() => setField('quality_grade', q)} testId={`wizard-quality-${q}`}>{q} escolha</ChoiceButton>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>Calibre</FieldLabel>
              <Input value={data.caliber} onChange={(e) => setField('caliber', e.target.value)} data-testid="wizard-caliber" />
            </div>
            <div>
              <FieldLabel>Tonalidade</FieldLabel>
              <Input value={data.tonality} onChange={(e) => setField('tonality', e.target.value)} data-testid="wizard-tonality" />
            </div>
          </div>
        );
      case 7:
        return (
          <div>
            <FieldLabel>Morada da Obra</FieldLabel>
            <Textarea value={data.work_address} onChange={(e) => setField('work_address', e.target.value)} placeholder="Morada completa do local da obra" data-testid="wizard-work-address" />
          </div>
        );
      case 8:
        return (
          <div>
            <FieldLabel>Contacto do Responsável da Obra</FieldLabel>
            <Input value={data.work_contact} onChange={(e) => setField('work_contact', e.target.value)} placeholder="Nome, telefone ou email" data-testid="wizard-work-contact" />
          </div>
        );
      case 9:
        return (
          <div className="space-y-5">
            <div>
              <FieldLabel>Ambiente</FieldLabel>
              <div className="flex gap-3">
                <ChoiceButton active={data.application_environment === 'Interior'} onClick={() => setField('application_environment', 'Interior')} testId="wizard-env-interior">Interior</ChoiceButton>
                <ChoiceButton active={data.application_environment === 'Exterior'} onClick={() => setField('application_environment', 'Exterior')} testId="wizard-env-exterior">Exterior</ChoiceButton>
              </div>
            </div>
            <div>
              <FieldLabel>Superfície</FieldLabel>
              <div className="flex gap-3">
                <ChoiceButton active={data.application_surface === 'Chão'} onClick={() => setField('application_surface', 'Chão')} testId="wizard-surface-chao">Chão</ChoiceButton>
                <ChoiceButton active={data.application_surface === 'Parede'} onClick={() => setField('application_surface', 'Parede')} testId="wizard-surface-parede">Parede</ChoiceButton>
              </div>
            </div>
          </div>
        );
      case 10:
        return (
          <div>
            <FieldLabel>Data de Aplicação do Produto</FieldLabel>
            <Input type="date" value={data.application_date} onChange={(e) => setField('application_date', e.target.value)} data-testid="wizard-application-date" />
          </div>
        );
      case 11:
        return (
          <div className="space-y-4">
            <div>
              <FieldLabel>Tipo de Amostra Recolhida</FieldLabel>
              <Input value={data.samples_type} onChange={(e) => setField('samples_type', e.target.value)} placeholder="Ex: Peça inteira" data-testid="wizard-samples-type" />
            </div>
            <div>
              <FieldLabel>Local de Recolha</FieldLabel>
              <Input value={data.samples_location} onChange={(e) => setField('samples_location', e.target.value)} placeholder="Ex: Cozinha" data-testid="wizard-samples-location" />
            </div>
          </div>
        );
      case 12:
        return (
          <div className="space-y-5">
            <div>
              <FieldLabel>Houve Visita à Obra?</FieldLabel>
              <div className="flex gap-3">
                <ChoiceButton active={data.work_visit_done === true} onClick={() => setField('work_visit_done', true)} testId="wizard-work-visit-sim">Sim</ChoiceButton>
                <ChoiceButton active={data.work_visit_done === false} onClick={() => { setField('work_visit_done', false); setField('work_visit_date', ''); setField('work_visit_time', ''); }} testId="wizard-work-visit-nao">Não</ChoiceButton>
              </div>
            </div>
            {data.work_visit_done === true && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Data da Visita</FieldLabel>
                  <Input type="date" value={data.work_visit_date} onChange={(e) => setField('work_visit_date', e.target.value)} data-testid="wizard-work-visit-date" />
                </div>
                <div>
                  <FieldLabel>Hora da Visita</FieldLabel>
                  <Input type="time" value={data.work_visit_time} onChange={(e) => setField('work_visit_time', e.target.value)} data-testid="wizard-work-visit-time" />
                </div>
              </div>
            )}
          </div>
        );
      case 13:
        return (
          <div>
            <FieldLabel>Parecer do Vendedor (opcional)</FieldLabel>
            <Textarea value={data.seller_opinion} onChange={(e) => setField('seller_opinion', e.target.value)} placeholder="Avaliação do vendedor sobre a reclamação" rows={4} data-testid="wizard-seller-opinion" />
          </div>
        );
      case 14:
        return (
          <div>
            <FieldLabel>Sugestões do Cliente (opcional)</FieldLabel>
            <Textarea value={data.improvement_suggestions} onChange={(e) => setField('improvement_suggestions', e.target.value)} placeholder="Pode deixar em branco" rows={4} data-testid="wizard-improvement-suggestions" />
          </div>
        );
      case 15:
        return (
          <div className="space-y-5">
            {isEditMode ? (
              <>
                <div>
                  <FieldLabel>Fotografias da Reclamação</FieldLabel>
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl py-8 cursor-pointer hover:border-[#009392] transition-colors">
                    <UploadSimple size={28} className="text-stone-400" />
                    <span className="text-sm text-stone-500">{uploadingPhoto ? "A carregar..." : "Clique para anexar fotos (pode selecionar várias)"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} data-testid="wizard-photo-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="wizard-photos-grid">
                      {photos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`} alt={photo.name} className="w-full h-24 object-cover rounded-lg border border-stone-200" />
                          <button
                            type="button"
                            onClick={() => handlePhotoDelete(photo.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`wizard-photo-delete-${photo.id}`}
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="wizard-submit-complaint">
                  {submitting ? "A guardar..." : "Guardar Alterações"}
                </Button>
              </>
            ) : !complaintId ? (
              <div className="text-center py-6">
                <p className="text-stone-500 mb-4">Reveja os dados e submeta a reclamação. Depois poderá anexar fotografias.</p>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="wizard-submit-complaint">
                  {submitting ? "A submeter..." : "Submeter Reclamação"}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg px-4 py-3" data-testid="wizard-success-banner">
                  <CheckCircle size={20} weight="fill" />
                  <span className="font-semibold">Reclamação registada com sucesso!</span>
                </div>
                <div>
                  <FieldLabel>Fotografias da Reclamação</FieldLabel>
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl py-8 cursor-pointer hover:border-[#009392] transition-colors">
                    <UploadSimple size={28} className="text-stone-400" />
                    <span className="text-sm text-stone-500">{uploadingPhoto ? "A carregar..." : "Clique para anexar fotos (pode selecionar várias)"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} data-testid="wizard-photo-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="wizard-photos-grid">
                      {photos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`} alt={photo.name} className="w-full h-24 object-cover rounded-lg border border-stone-200" />
                          <button
                            type="button"
                            onClick={() => handlePhotoDelete(photo.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`wizard-photo-delete-${photo.id}`}
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={() => navigate(`/reclamacoes/${complaintId}`)} className="w-full bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="wizard-finish-btn">
                  Concluir e Ver Ficha da Reclamação
                </Button>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const progress = ((step + 1) / totalSteps) * 100;

  if (loadingComplaint) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="wizard-loading">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-24 md:pb-6" data-testid="reclamacao-wizard-page">
      <PageHeader
        icon={Flag}
        title={isEditMode ? "Editar Reclamação" : "Nova Reclamação"}
        subtitle={isEditMode ? "Atualize os dados desta reclamação" : "Assistente de registo de reclamação"}
        onBack={() => navigate(isEditMode ? `/reclamacoes/${routeComplaintId}` : '/reclamacoes')}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-stone-500">
          <span data-testid="wizard-step-counter">Passo {step + 1} de {totalSteps}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      <div className="gresart-card" data-testid="wizard-step-card">
        <h2 className="text-lg font-bold text-stone-900 mb-1">{STEPS_META[step].title}</h2>
        <p className="text-sm text-stone-500 mb-5">{STEPS_META[step].desc}</p>
        {renderStep()}
      </div>

      {step < totalSteps - 1 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={goBack} disabled={step === 0} data-testid="wizard-back-btn">
            <ArrowLeft size={16} /> Voltar
          </Button>
          <Button onClick={goNext} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="wizard-next-btn">
            Avançar <ArrowRight size={16} />
          </Button>
        </div>
      )}
      {step === totalSteps - 1 && (!complaintId || isEditMode) && (
        <div className="flex items-center justify-start">
          <Button variant="outline" onClick={goBack} data-testid="wizard-back-btn-last">
            <ArrowLeft size={16} /> Voltar
          </Button>
        </div>
      )}
    </div>
  );
}
